import { generatePath } from "react-router-dom";

import { flow } from "fp-ts/lib/function";

import {
    type CameraKitOrganization,
    type MyLensesOrganization as LensCentralOrganization,
    type LensSpace,
} from "../../gql-generated/types";
import { type OrganizationsList } from "../../shared/api/queries";
import { ALL_ORGS_FEATURES, CAMERAKIT_FEATURES, LENS_FOLDER_FEATURES } from "../../shared/models";
import { type Container, type OrganizationContainer } from "../models";
import { AbsoluteRoute } from "../routes/absoluteRoutes";

import { sortOrganizationContainers } from "./sortOrganizationContainers";

export const getDefaultOrganizationContainer = (
    organizationContainers: OrganizationContainer[],
    defaultOrganizationId: string,
): OrganizationContainer | undefined => {
    const defaultOrganizationContainer =
        organizationContainers.find(({ id }) => id === defaultOrganizationId) ?? organizationContainers[0];
    return defaultOrganizationContainer;
};

export const sortLensFoldersByName = (lensFolders: readonly LensSpace[]): LensSpace[] => {
    return [...lensFolders].sort((a, b) => a.name.localeCompare(b.name));
};

export const makeOrganizationsMap = (organizations: OrganizationsList): Map<string, LensCentralOrganization> => {
    const orgMap = new Map<string, LensCentralOrganization>();
    for (const org of organizations) {
        const lensSpacesList = sortLensFoldersByName(org.lensSpacesList);
        const orgWithSortedLensSpacesList = {
            ...org,
            lensSpacesList,
        };
        orgMap.set(org.id, orgWithSortedLensSpacesList);
    }

    return orgMap;
};

export const makeAllContainersMap = (organizationContainers: OrganizationContainer[]): Map<string, Container> => {
    const organizationContainersMap = new Map<string, Container>();

    for (const parentContainer of organizationContainers) {
        organizationContainersMap.set(parentContainer.id, parentContainer);
        for (const childContainer of parentContainer.lensFolders) {
            organizationContainersMap.set(childContainer.id, childContainer);
        }
    }

    return organizationContainersMap;
};

const mapLensCentralOrganizationContainer = (organization: LensCentralOrganization): OrganizationContainer => {
    const { id, name, lensSpacesList } = organization;

    const lensCentralFeatures = [...LENS_FOLDER_FEATURES, ...ALL_ORGS_FEATURES];
    const lensFolders = lensSpacesList.map((lf) => ({
        ...lf,
        features: new Set(lensCentralFeatures),
        path: generatePath(AbsoluteRoute.ORG_LENS_FOLDER, {
            orgId: id,
            lensFolderId: lf.id,
        }),
    }));

    const features = new Set(lensCentralFeatures);

    return {
        id,
        name,
        lensFolders,
        features,
    };
};

const mapCameraKitOrganizationsContainer = (
    organization: CameraKitOrganization,
    includesAllOrgs: boolean,
): OrganizationContainer => {
    const { id, name } = organization;

    const features = new Set([...(includesAllOrgs ? [] : CAMERAKIT_FEATURES), ...ALL_ORGS_FEATURES]);

    return {
        id: id ?? "",
        name: name ?? "",
        lensFolders: [],
        features,
    };
};

const addLensCentralOrganizationContainers =
    (lensCentralOrganizations: LensCentralOrganization[]) =>
    (organizationContainers: OrganizationContainer[]): OrganizationContainer[] => {
        const lensCentralContainers = lensCentralOrganizations.map((org) => mapLensCentralOrganizationContainer(org));
        return [...lensCentralContainers, ...organizationContainers];
    };

const addCamkitOrganizationContainers =
    (camkitOrganizations: CameraKitOrganization[], includesAllOrgs: boolean) =>
    (organizationContainers: OrganizationContainer[]): OrganizationContainer[] => {
        const camkitContainers: OrganizationContainer[] = camkitOrganizations.map((org) =>
            mapCameraKitOrganizationsContainer(org, includesAllOrgs),
        );

        const resultMap = new Map<string, OrganizationContainer>();
        // add existing containers
        for (const container of organizationContainers) {
            resultMap.set(container.id, container);
        }

        // merge Camerakit containers
        for (const container of camkitContainers) {
            const lensCentralContainer = resultMap.get(container.id);
            if (lensCentralContainer === undefined) {
                resultMap.set(container.id, container);
            } else {
                const allFeatures = [...lensCentralContainer.features, ...container.features];
                const lensFolders = lensCentralContainer.lensFolders.map((lf) => ({
                    ...lf,
                    features: new Set(allFeatures),
                }));
                const mergedContainer: OrganizationContainer = {
                    ...lensCentralContainer,
                    lensFolders,
                    features: new Set(allFeatures),
                };
                resultMap.set(container.id, mergedContainer);
            }
        }

        return [...resultMap.values()];
    };

export const makeOrganizationContainers = (
    lensCentralOrganizations: LensCentralOrganization[],
    cameraKitOrganizations: CameraKitOrganization[],
    allOrganizations: CameraKitOrganization[],
    defaultOrganizationId: string,
): OrganizationContainer[] => {
    const result: OrganizationContainer[] = [];

    return flow(
        addLensCentralOrganizationContainers(lensCentralOrganizations),
        addCamkitOrganizationContainers(cameraKitOrganizations, false),
        addCamkitOrganizationContainers(allOrganizations, true),
        sortOrganizationContainers(defaultOrganizationId),
    )(result);
};
